import { useState, useRef } from "react";
import { t, font, Button, Modal } from "@realtyflows/ui";
import { importClients } from "../api";

/**
 * ImportClientsModal — V1 CSV bulk-import flow.
 *
 *   File pick → parse in browser → preview the first 10 rows + total count
 *   → user confirms → POST to /clients/import → show summary.
 *
 * Parsing intentionally has no dependency on PapaParse / xlsx. The expected
 * shape is small (8 fields, all strings), so a forgiving line-by-line parser
 * is enough. Quotes are honored so "Smith, John" works as a single field.
 */
const FIELDS = ["name", "email", "phone", "source", "budget", "location", "timeline", "notes"];
const PREVIEW_ROWS = 10;
const MAX_ROWS = 1000;

// Minimal RFC-4180-ish CSV parser. Handles quoted fields, escaped quotes (""),
// and trims surrounding whitespace. Doesn't try to be exhaustive — refuses
// gracefully on shapes it can't make sense of.
function parseCsv(text) {
  const lines = [];
  let cur = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') { field += '"'; i++; continue; }
      if (ch === '"') { inQuotes = false; continue; }
      field += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { cur.push(field); field = ""; continue; }
    if (ch === '\n' || ch === '\r') {
      // Skip \r\n duplicate
      if (ch === '\r' && next === '\n') i++;
      cur.push(field);
      lines.push(cur);
      cur = [];
      field = "";
      continue;
    }
    field += ch;
  }
  if (field.length > 0 || cur.length > 0) {
    cur.push(field);
    lines.push(cur);
  }
  // Drop trailing fully-empty rows (final newline artifact)
  while (lines.length && lines[lines.length - 1].every((c) => !c.trim())) lines.pop();
  return lines;
}

// Map CSV headers to our known field names. Forgiving — case-insensitive,
// strips spaces/underscores/hyphens. Unknown headers are dropped silently
// so users don't have to clean an exported sheet before uploading.
function mapHeaders(headerRow) {
  const normalize = (s) => (s || "").toString().toLowerCase().replace(/[\s_-]+/g, "");
  const aliases = {
    name: ["name", "fullname", "clientname", "firstandlast"],
    email: ["email", "emailaddress", "mail"],
    phone: ["phone", "phonenumber", "mobile", "cell"],
    source: ["source", "leadsource", "from"],
    budget: ["budget", "price", "pricerange"],
    location: ["location", "area", "preferredarea", "city", "neighborhood"],
    timeline: ["timeline", "movetimeline", "when"],
    notes: ["notes", "note", "comments"],
  };
  const map = {};
  headerRow.forEach((raw, i) => {
    const norm = normalize(raw);
    for (const field of FIELDS) {
      if (aliases[field].includes(norm)) {
        map[field] = i;
        break;
      }
    }
  });
  return map;
}

export default function ImportClientsModal({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);          // parsed + mapped rows ready to POST
  const [skipped, setSkipped] = useState([]);    // rows we couldn't read locally
  const [headerMap, setHeaderMap] = useState({});
  const [step, setStep] = useState("pick");      // pick → preview → done
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setRows([]); setSkipped([]); setHeaderMap({});
    setStep("pick"); setError(""); setResult(null); setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("File too large (max 5MB).");
      return;
    }

    let text;
    try {
      text = await file.text();
    } catch (err) {
      setError(`Could not read file: ${err.message}`);
      return;
    }

    const parsed = parseCsv(text);
    if (parsed.length < 2) {
      setError("CSV must have a header row plus at least one data row.");
      return;
    }

    const header = parsed[0];
    const map = mapHeaders(header);
    if (Object.keys(map).length === 0) {
      setError(`No recognized columns. Expected at least one of: ${FIELDS.join(", ")}.`);
      return;
    }
    if (!map.name && !map.email) {
      setError(`CSV must include at least a "name" or "email" column.`);
      return;
    }

    const localSkipped = [];
    const valid = [];
    for (let i = 1; i < parsed.length; i++) {
      const row = parsed[i];
      // Empty row (e.g. trailing blank line) → silently skip, don't surface as a "skip" to the user.
      if (row.every((c) => !c.trim())) continue;

      const obj = {};
      for (const field of FIELDS) {
        if (map[field] !== undefined) obj[field] = (row[map[field]] || "").trim();
      }
      if (!obj.name && !obj.email) {
        localSkipped.push({ index: i, reason: "Missing both name and email" });
        continue;
      }
      valid.push(obj);
    }

    if (valid.length === 0) {
      setError("No importable rows after parsing — every row was missing a name and email.");
      return;
    }
    if (valid.length > MAX_ROWS) {
      setError(`Too many rows (${valid.length}). Max ${MAX_ROWS} per import.`);
      return;
    }

    setHeaderMap(map);
    setRows(valid);
    setSkipped(localSkipped);
    setStep("preview");
  };

  const runImport = async () => {
    setImporting(true);
    setError("");
    try {
      const res = await importClients(rows);
      setResult(res);
      setStep("done");
      onImported?.();
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} title="Import clients from CSV" width={720}>
      {step === "pick" && (
        <div style={{ padding: "12px 4px", fontFamily: font.body, fontSize: 14, color: t.textSecondary }}>
          <p style={{ marginTop: 0 }}>
            Upload a CSV with a header row. We'll match these columns
            (case-insensitive, in any order):
          </p>
          <ul style={{ marginTop: 6, paddingLeft: 22 }}>
            {FIELDS.map((f) => <li key={f}><code style={codeStyle}>{f}</code></li>)}
          </ul>
          <p style={{ color: t.textMuted, fontSize: 13 }}>
            At minimum, include either <code style={codeStyle}>name</code> or{' '}
            <code style={codeStyle}>email</code>. Duplicates are matched by email
            and merged into the existing client.
          </p>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            style={{ marginTop: 12 }}
          />

          {error && (
            <div style={errorStyle}>{error}</div>
          )}
        </div>
      )}

      {step === "preview" && (
        <div style={{ padding: "12px 4px", fontFamily: font.body }}>
          <div style={{ fontSize: 14, color: t.textPrimary, marginBottom: 12 }}>
            Parsed <strong>{rows.length}</strong> row{rows.length === 1 ? "" : "s"}.
            {skipped.length > 0 && (
              <span style={{ color: t.amber, marginLeft: 8 }}>
                Skipping {skipped.length} (no name/email).
              </span>
            )}
            <span style={{ color: t.textMuted, marginLeft: 8 }}>
              Columns matched: {Object.keys(headerMap).join(", ")}
            </span>
          </div>

          <div style={{ maxHeight: 280, overflow: "auto", border: `1px solid ${t.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead style={{ background: t.surfaceWarm, position: "sticky", top: 0 }}>
                <tr>
                  {FIELDS.filter((f) => headerMap[f] !== undefined).map((f) => (
                    <th key={f} style={thStyle}>{f}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, PREVIEW_ROWS).map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                    {FIELDS.filter((f) => headerMap[f] !== undefined).map((f) => (
                      <td key={f} style={tdStyle}>{r[f] || <span style={{ color: t.textFaint }}>—</span>}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > PREVIEW_ROWS && (
              <div style={{ padding: 8, textAlign: "center", color: t.textMuted, fontSize: 12, background: t.surfaceWarm }}>
                …and {rows.length - PREVIEW_ROWS} more
              </div>
            )}
          </div>

          {error && <div style={errorStyle}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <Button variant="ghost" onClick={() => setStep("pick")} disabled={importing}>Back</Button>
            <Button variant="primary" onClick={runImport} disabled={importing}>
              {importing ? "Importing..." : `Import ${rows.length} client${rows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div style={{ padding: "12px 4px", fontFamily: font.body }}>
          <div style={{ fontSize: 14, color: t.textPrimary, marginBottom: 12 }}>
            Import complete.
          </div>
          <ul style={{ paddingLeft: 22, lineHeight: 1.8 }}>
            <li><strong>{result.created}</strong> new client{result.created === 1 ? "" : "s"} created</li>
            <li><strong>{result.updated}</strong> existing client{result.updated === 1 ? "" : "s"} updated</li>
            <li><strong>{result.skipped?.length || 0}</strong> row{(result.skipped?.length || 0) === 1 ? "" : "s"} skipped</li>
          </ul>
          {result.skipped?.length > 0 && (
            <details style={{ marginTop: 10, fontSize: 13, color: t.textSecondary }}>
              <summary style={{ cursor: "pointer" }}>Why were rows skipped?</summary>
              <ul style={{ paddingLeft: 22, marginTop: 6 }}>
                {result.skipped.slice(0, 20).map((s, i) => (
                  <li key={i}>Row {s.index + 2}: {s.reason}</li>
                ))}
                {result.skipped.length > 20 && <li>…and {result.skipped.length - 20} more</li>}
              </ul>
            </details>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <Button variant="primary" onClick={handleClose}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const codeStyle = {
  fontFamily: "ui-monospace, monospace",
  fontSize: 12,
  background: t.surfaceWarm,
  padding: "2px 6px",
  borderRadius: 4,
};

const errorStyle = {
  marginTop: 12, padding: "10px 14px",
  background: t.coralLight, color: t.coral,
  border: `1px solid ${t.coral}30`, borderRadius: 6,
  fontSize: 13,
};

const thStyle = {
  padding: "8px 10px", textAlign: "left",
  fontWeight: 700, color: t.textSecondary,
  fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5,
};

const tdStyle = {
  padding: "6px 10px", color: t.textPrimary, fontSize: 12,
  maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
